/* B2: PDP gallery video autoplay controller (Cecobox).
   Plays the gallery's hosted video only when its slide is active, substantially
   visible, the tab is visible, no modal is open, reduced-motion is off, and the
   user has not manually paused. Never touches the modal or noscript instances. */
(function () {
  'use strict';
  if (window.__b2VideoAutoplay) return; // script tag is emitted per video media; run once
  window.__b2VideoAutoplay = true;
  if (!document.body.classList.contains('template-product')) return; // PDP only

  var init = function () {
    var gallery = document.querySelector('.product__media-list.js-media-list');
    if (!gallery) return;
    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Hosted-video deferred-media in the active gallery only (structural scope:
    // never by id — poster ids are duplicated across gallery/noscript/modal).
    var hosts = Array.prototype.filter.call(
      gallery.querySelectorAll('deferred-media'),
      function (el) {
        if (el.closest('.product__media-grid-noscript')) return false;
        var tpl = el.querySelector('template');
        return !!(tpl && tpl.content && tpl.content.querySelector('video'));
      }
    );
    if (!hosts.length) return;

    var state = new Map();   // host -> pause/interaction bookkeeping

    hosts.forEach(function (h) {
      state.set(h, { userPaused: false, finished: false, ourPause: false, ourPlay: false, lastInteract: 0 });
      // Autoplay policy: programmatic play() is only allowed muted. The cuts are
      // silent (no audio stream); the muted attribute survives template cloning.
      var tv = h.querySelector('template').content.querySelector('video');
      if (tv) tv.setAttribute('muted', '');
      ['pointerdown', 'keydown'].forEach(function (t) {
        h.addEventListener(t, function () { state.get(h).lastInteract = performance.now(); }, true);
      });
    });

    var eligible = function (h) {
      var s = state.get(h);
      if (motion.matches) return false;
      if (s.userPaused || s.finished) return false;
      // Live geometry, normalized to the smaller of element/viewport per axis:
      // a gallery taller than the viewport (short laptops, projectors) can never
      // reach 60% raw ratio — the 2026-09-19 field defect. Require 60% of what
      // CAN be shown instead.
      var q = h.getBoundingClientRect();
      var vw = window.innerWidth, vh = window.innerHeight;
      var ix = Math.max(0, Math.min(q.right, vw) - Math.max(q.left, 0));
      var iy = Math.max(0, Math.min(q.bottom, vh) - Math.max(q.top, 0));
      var denom = Math.min(q.width, vw) * Math.min(q.height, vh);
      if (!denom || (ix * iy) / denom < 0.6) return false;
      if (document.visibilityState !== 'visible') return false;
      if (document.body.classList.contains('overflow-hidden')) return false;
      // This Swiper build does not apply swiper-slide-active; use the instance.
      // Fail closed: no proven active-slide identity means not eligible.
      var slide = h.closest('.swiper-slide');
      var sw = gallery.swiper;
      if (!slide || !sw || !sw.slides || sw.slides[sw.activeIndex] !== slide) return false;
      return true;
    };

    var wireVideo = function (h) {
      var v = h.querySelector('video');
      if (!v || v.dataset.b2Wired) return v;
      v.dataset.b2Wired = '1';
      v.muted = true; // property does not survive cloning; silent source, safe
      // Safari cure (2026-09-19, causally proven via safaridriver on the
      // operator's Safari): with preload="none", Safari suspends after
      // loadstart and play() does NOT start the fetch - the video stalls
      // indefinitely (observed 34s; fetch began 151ms after this change).
      // wireVideo only runs through gated paths, so non-viewers still fetch
      // nothing - the markup keeps preload="none".
      v.preload = 'auto';
      // Perceived-latency guard: the theme hides the poster the instant
      // loadContent runs, exposing an undecoded (blank) video. Keep the poster
      // painted (CSS keys off b2-video-ready) until a real frame exists.
      var markReady = function () {
        requestAnimationFrame(function () { h.classList.add('b2-video-ready'); });
      };
      if (v.readyState >= 2) markReady(); // HAVE_CURRENT_DATA
      else v.addEventListener('loadeddata', markReady, { once: true });
      v.addEventListener('pause', function () {
        var s = state.get(h);
        if (s.ourPause) { s.ourPause = false; s.lastInteract = 0; return; }
        // Pause within 1s of a pointer/key interaction on this media = user intent (sticky).
        if (performance.now() - s.lastInteract < 1000) s.userPaused = true;
        // Otherwise programmatic (pauseAllMedia): non-sticky; reconcile() decides later.
        s.lastInteract = 0; // consume: a later programmatic pause must not inherit intent
      });
      v.addEventListener('play', function () {
        var s = state.get(h);
        if (s.ourPlay) { s.ourPlay = false; s.lastInteract = 0; return; }
        s.userPaused = false; s.finished = false; // explicit user play clears sticky states
        s.lastInteract = 0; // consume the interaction that started playback
      });
      v.addEventListener('ended', function () { state.get(h).finished = true; });
      return v;
    };

    var playHost = function (h) {
      var s = state.get(h);
      if (!h.getAttribute('loaded')) {
        // loadContent() clones the template and plays; only ever invoked
        // through the full eligibility gate (checked by the caller).
        // Cold-entry fix (2026-09-08): the theme's single play() can be
        // interrupted (observed live: AbortError; video left loaded+buffered
        // but paused). Schedule exactly two bounded settle checks through the
        // full reconcile gate — no-ops if playback survived, and the 600ms one
        // outlasts the theme's 500ms Swiper transition.
        if (typeof h.loadContent === 'function') {
          h.loadContent();
          var lv = wireVideo(h);
          if (h.getAttribute('loaded') && lv) {
            requestAnimationFrame(reconcile);
            setTimeout(reconcile, 600);
          }
        }
        return;
      }
      var v = wireVideo(h);
      if (v && v.paused && !s.ourPlay) {
        s.ourPlay = true;
        var p = v.play();
        // Clear the marker on settlement either way (the play event also clears
        // it, but a suspended promise must not latch the guard shut).
        if (p && p.then) p.then(function () { s.ourPlay = false; }, function () { s.ourPlay = false; });
      }
    };

    var pauseHost = function (h) {
      var v = h.querySelector('video');
      if (v && !v.paused) { state.get(h).ourPause = true; v.pause(); }
    };

    // Level-based reconcile, fired only on discrete events (IO, scroll/resize,
    // visibility, modal close, slide change) — playHost/pauseHost are idempotent,
    // so a rejected play() is retried at most once per subsequent event, never in a loop.
    var reconcile = function () {
      var claimed = false; // at most one controller-owned playing video
      hosts.forEach(function (h) {
        var ok = eligible(h) && !claimed;
        if (ok) claimed = true;
        if (ok) playHost(h); else pauseHost(h);
      });
    };

    // IO is a wake-up source only; eligibility reads live geometry above.
    var io = new IntersectionObserver(function () { reconcile(); }, { threshold: [0, 0.25, 0.5, 0.6] });
    hosts.forEach(function (h) { io.observe(h); });
    // Exact boundary transitions need scroll/resize reconciliation (rAF-coalesced).
    var rafPending = false;
    var onScrollOrResize = function () {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () { rafPending = false; reconcile(); });
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onScrollOrResize);

    document.addEventListener('visibilitychange', reconcile);
    document.body.addEventListener('modalClosed', reconcile);
    if (motion.addEventListener) motion.addEventListener('change', reconcile);
    // Slide activation: hook the Swiper instance (it may initialize after us).
    // slideChange fires synchronously on activeIndex change; deferring one tick
    // lets the theme's own slideChange pauseAllMedia handler run first.
    var onSlide = function () { setTimeout(reconcile, 0); };
    var hooked = false;
    var hookSwiper = function () {
      if (hooked || !gallery.swiper) return;
      hooked = true;
      gallery.swiper.on('slideChange', onSlide);
      reconcile();
    };
    hookSwiper();
    if (!hooked) {
      var tries = 0;
      var t = setInterval(function () {
        hookSwiper();
        if (hooked || ++tries > 40) clearInterval(t); // give up after ~10s
      }, 250);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
